import { createSelector } from 'reselect';
import { parseISO, format } from 'date-fns';

import { selectActiveVenueId } from 'selectors/general';

import {
  venueOpportunities as venueOpportunitiesEntity,
  venueSmartSpotOpportunities as venueSmartSpotOpportunitiesEntity,
} from 'store/saga/entities/bound';

const selectOpportunitiesEntity = createSelector(
  state => state,
  selectActiveVenueId,
  (state, activeVenueId) => venueOpportunitiesEntity.select(state, activeVenueId)
);

const selectSmartSpotOpportunitiesEntity = createSelector(
  state => state,
  selectActiveVenueId,
  (state, activeVenueId) => venueSmartSpotOpportunitiesEntity.select(state, activeVenueId)
);

export const selectOpportunities = createSelector(
  [selectOpportunitiesEntity, selectSmartSpotOpportunitiesEntity, selectActiveVenueId],
  (traditionalOpportunities, smartSpotOpportunities, activeVenueId) => {
    const traditionalData = traditionalOpportunities.data || [];
    const smartSpotData = smartSpotOpportunities.data || [];

    // Combine both opportunity sources
    const allOpportunities = [
      ...traditionalData.map(opportunity => ({
        ...opportunity,
        opportunitySource: 'LEGACY', // Default to LEGACY for backward compatibility
        startDateTime: parseISO(`${opportunity.classDate} ${opportunity.startTime}`),
      })),
      ...smartSpotData.map(opportunity => {
        // Convert Date objects from protobuf to ISO strings
        const startTimeStr =
          opportunity.startTime instanceof Date ? opportunity.startTime.toISOString() : opportunity.startTime;

        const endTimeStr =
          opportunity.endTime instanceof Date ? opportunity.endTime.toISOString() : opportunity.endTime;

        const localTimestamp = startTimeStr.replace('Z', '');
        const startDateTime = parseISO(localTimestamp);
        const classDate = format(startDateTime, 'yyyy-MM-dd');
        const extractedTime = format(startDateTime, 'HH:mm:ss');

        const { spotData = {} } = opportunity;

        const totalCapacity = spotData.total || 0;
        const totalReservations = spotData.takenSpots || spotData.booked || 0;
        const availableSpots = Math.max(0, totalCapacity - totalReservations);
        const rawRecommendation = opportunity.recommendedSpots || 1;
        const cappedRecommendation = Math.min(rawRecommendation, availableSpots);

        return {
          ...opportunity,
          opportunitySource: 'SMARTSPOT',
          startDateTime,
          classDate,
          startTime: extractedTime,
          endTime: endTimeStr,
          venueId: activeVenueId,
          scheduleId: opportunity.scheduleId,
          classId: opportunity.classId,
          id: opportunity.scheduleId,
          className: opportunity.className,
          totalCapacity,
          totalReservations,
          totalClasspassReservations: spotData.classpassBooked || 0,
          totalClasspassCapacity: spotData.classpassTotal || 0,
          classpassDemand: spotData.classpassDemand || 0,
          teacherName: opportunity.instructorName,
          recommendation: cappedRecommendation,
          waitingClients: opportunity.waitingClients || 0,
        };
      }),
    ];

    // Sort all opportunities by start time (chronologically)
    const sortedOpportunities = allOpportunities.sort((a, b) => a.startDateTime - b.startDateTime);

    return {
      data: sortedOpportunities,
      error: traditionalOpportunities.error || smartSpotOpportunities.error,
      isLoaded: traditionalOpportunities.loaded && smartSpotOpportunities.loaded,
      isLoading: traditionalOpportunities.loading || smartSpotOpportunities.loading,
    };
  }
);

export const selectOpportunitiesByType = createSelector(selectOpportunities, ({ data }) =>
  data.reduce(
    (memo, opportunity) => {
      if (opportunity.classDisabled) {
        memo.classOpportunities.push(opportunity);
      } else if (opportunity.opportunitySource === 'SMARTSPOT') {
        memo.smartSpotOpportunities.push(opportunity);
      } else {
        memo.legacyOpportunities.push(opportunity);
      }
      return memo;
    },
    {
      classOpportunities: [],
      legacyOpportunities: [], // Manual + Integrated venues (LEGACY)
      smartSpotOpportunities: [], // SmartSpot venues (SMARTSPOT)
    }
  )
);

export const selectOpportunitiesTeaserMeta = state => state.opportunitiesTeaser;
